import { outreach } from '@round/api';
import { getTableMetaHelper } from '@round/ui-kit';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { tableHelpers } from '@round/creatorbase-ui';
import { mapOrderingToTableSorting, mapTableSortingToOrdering } from '@round/whitelabel-ui';
import { formatNumberToKNotation } from 'helpers';
import { OutreachResultsTab } from './components/OutreachResultsTabs/helpers';

export const PAGE_SIZE = 25;

export const NO_VALUE = '—';

/** `created_at` has no column of its own: it is the fallback ordering, not one a header can ask for. */
const SORTABLE_COLUMN_IDS = ['created_at', 'follower_count', 'engagement_rate', 'avg_views'] as const;

type SortableColumnId = (typeof SORTABLE_COLUMN_IDS)[number];

export const DEFAULT_ORDERING: outreach.OutreachResultOrdering = '-created_at';

const isSortableColumnId = (id: string): id is SortableColumnId => SORTABLE_COLUMN_IDS.includes(id as SortableColumnId);

export function parseOrdering(ordering: string | undefined): outreach.OutreachResultOrdering {
    const [sort] = mapOrderingToTableSorting(ordering ? [ordering] : []);
    if (!sort || !isSortableColumnId(sort.id)) {
        return DEFAULT_ORDERING;
    }

    return ordering as outreach.OutreachResultOrdering;
}

export function getSorting(ordering: string | undefined): SortingState {
    return mapOrderingToTableSorting([parseOrdering(ordering)]);
}

export function getOrdering(sorting: SortingState): outreach.OutreachResultOrdering {
    return parseOrdering(mapTableSortingToOrdering(sorting)[0]);
}

export type OutreachResultRow = outreach.OutreachResult;

export const ADD_NEEDS_TAGS_HINT = 'Tag this creator before adding them';

/** Adding a result still in review is refused until it carries a tag. */
export const needsTagsToAdd = (row: Pick<OutreachResultRow, 'status' | 'content_tags'>) =>
    row.status === 'TO_REVIEW' && !row.content_tags.length;

export type Meta = {
    isLoading: boolean;
    tab: OutreachResultsTab;
    getAvatarUrl: (userId: number) => string | undefined;
    areAvatarsLoading: boolean;
    /** The rows a bulk action is to run over, keyed on the result id — the table's row key. */
    selectedIds: Set<number>;
    onToggleResult: (id: number) => void;
    onSelectResults: (ids: number[]) => void;
    onClearSelection: () => void;
    onOpenCreatorDetails: (clientInfluencerAccountId: number) => void;
};

export const getMeta = getTableMetaHelper<Meta>();

// `any` (not `unknown`) so string-valued cells like tableHelpers.TruncatedCell,
// whose context is typed to a string value, satisfy the shared column type.
export type Column = ColumnDef<tableHelpers.WithLoadingRow<OutreachResultRow>, any>;

export const getProfileUrl = (username: string) => `https://www.tiktok.com/@${username}`;

const MAX_INITIALS = 2;

/** A handle carries no spaces, so the word breaks are the separators a username is allowed to use. */
export const getInitials = (username: string) =>
    username
        .split(/[\s._-]+/)
        .filter(Boolean)
        .slice(0, MAX_INITIALS)
        .map((part) => part[0].toUpperCase())
        .join('');

const ENGAGEMENT_RATE_DECIMAL_PLACES = 1;

export const formatCount = (value: number | null | undefined) =>
    typeof value === 'number' ? formatNumberToKNotation(value) : NO_VALUE;

/** Served as a fraction, same as the request's own filter. */
export const formatEngagementRate = (value: number | null | undefined) =>
    typeof value === 'number' ? `${(value * 100).toFixed(ENGAGEMENT_RATE_DECIMAL_PLACES)}%` : NO_VALUE;
